import React from "react";
import axios from "axios";
import { Wallet, ListPlus, Waves } from "lucide-react";
import type { useToast } from "../ToastProvider/ToastProvider";
import { expenseTemplates } from "../../data/expenseTemplates";
import { useCreateExpense } from "../../hooks/useExpenses";

interface OnboardingStepsProps {
  open: boolean;
  wave1Amount: number;
  setWave1Amount: (amount: number) => void;
  wave2Amount: number;
  setWave2Amount: (amount: number) => void;
  wave3Amount: number;
  setWave3Amount: (amount: number) => void;
  wave4Amount: number;
  setWave4Amount: (amount: number) => void;
  setIsSetupComplete: (complete: boolean) => void;
  onboardingStep: number;
  setOnboardingStep: (step: number) => void;
  language: "en" | "es";
  t: any;
  toast: ReturnType<typeof useToast>;
  token: string | null;
  API_URL: string;
  onClose: () => void;
}

export default function OnboardingSteps({
  open,
  wave1Amount,
  setWave1Amount,
  //wave2Amount,
  setWave2Amount,
 // wave3Amount,
  setWave3Amount,
 // wave4Amount,
  setWave4Amount,
  setIsSetupComplete,
  onboardingStep,
  setOnboardingStep,
  language: _language,
  t,
  toast,
  token,
  API_URL,
  onClose,
}: OnboardingStepsProps) {
  const [showQuickAdd, setShowQuickAdd] = React.useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = React.useState<Set<string>>(new Set());
  const [isAddingTemplates, setIsAddingTemplates] = React.useState(false);
  const createExpenseMutation = useCreateExpense(token);

  const popularTemplates = React.useMemo(() => {
    return expenseTemplates
      .filter(t => t.priority <= 5)
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 10);
  }, []);

  const handleAddQuickTemplates = async () => {
    if (selectedTemplateIds.size === 0) {
      setShowQuickAdd(false);
      setOnboardingStep(2);
      return;
    }

    setIsAddingTemplates(true);
    try {
      const templatesToAdd = expenseTemplates.filter(t => selectedTemplateIds.has(t.id));

      for (const template of templatesToAdd) {
        const expenseData = {
          name: _language === 'es' ? template.nameEs : template.name,
          price: template.price || 0,
          dueDay: template.suggestedDueDay,
          category: 'Wave',
          paymentType: template.paymentType,
          paymentUrl: template.paymentUrl || '',
          paymentWave: template.suggestedWave,
          isRecurring: template.isRecurring,
          recurrenceFrequency: template.recurrenceFrequency,
        };

        await createExpenseMutation.mutateAsync(expenseData);
      }

      toast.success(`Added ${selectedTemplateIds.size} expenses!`);
      setSelectedTemplateIds(new Set());
      setShowQuickAdd(false);
      setOnboardingStep(2);
    } catch (err) {
      console.error('Failed to add templates:', err);
      toast.error('Failed to add some expenses');
    } finally {
      setIsAddingTemplates(false);
    }
  };

  const completeOnboarding = async () => {
    if (wave1Amount <= 0) {
      toast.error(t.onboardingSteps.validAmount);
      return;
    }
    try {
      // ✅ Save BOTH waveAmount AND isSetupComplete
      await axios.put(
        `${API_URL}/settings`,
        {
          wave1Amount,
          wave2Amount: wave1Amount,
          wave3Amount: wave1Amount,
          wave4Amount: wave1Amount,
          isSetupComplete: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWave2Amount(wave1Amount);
      setWave3Amount(wave1Amount);
      setWave4Amount(wave1Amount);
      setIsSetupComplete(true);
      onClose();
      toast.success(t.onboardingSteps.completeSuccess);
    } catch (err) {
      console.error("Failed to complete onboarding:", err);
      toast.error(t.onboardingSteps.completeError);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "90%",
          maxWidth: "600px",
          maxHeight: "80vh",
          overflowY: "auto",
          background: "var(--card-bg)",
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          padding: "2rem",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1.5rem",
            paddingBottom: "1rem",
            borderBottom: "2px solid var(--primary-color)",
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "var(--text-color)",
              fontSize: "1.5rem",
            }}
          >
            {t.onboardingSteps.welcomeTitle}
          </h2>
          <span style={{ color: "var(--primary-color)", fontWeight: "bold" }}>
            {onboardingStep + 1}/3
          </span>
        </div>
        {/* Step 1: Setup Wave Amount */}
        {onboardingStep === 0 && (
          <div style={{ animation: "fadeIn 0.3s ease-in" }}>
            <div style={{ textAlign: "center", marginBottom: "1rem" }}>
              <Wallet size={64} style={{ color: "var(--primary-color)" }} />
            </div>
            <h3 style={{ color: "var(--text-color)", marginBottom: "1rem" }}>
              {t.onboardingSteps.step1Title}
            </h3>
            <p
              style={{
                color: "var(--text-secondary)",
                lineHeight: 1.6,
                marginBottom: "1.5rem",
              }}
            >
              {t.onboardingSteps.step1Description}
            </p>
            <div
              style={{
                background: "var(--input-bg)",
                padding: "1.5rem",
                borderRadius: "12px",
                border: "2px solid var(--primary-color)",
                marginBottom: "1.5rem",
              }}
            >
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                  color: "var(--text-color)",
                }}
              >
                {t.onboardingSteps.enterPaycheckAmount}
              </label>
              <input
                type="number"
                className="input"
                placeholder="e.g., 2500"
                value={wave1Amount || ""}
                onChange={(e) =>
                  setWave1Amount(parseFloat(e.target.value) || 0)
                }
                style={{ fontSize: "18px", padding: "12px" }}
              />
              <p
                style={{
                  fontSize: "0.85rem",
                  color: "var(--text-secondary)",
                  marginTop: "0.5rem",
                }}
              >
                {t.onboardingSteps.examplePaycheck}
              </p>
            </div>
            <button
              onClick={async () => {
                if (wave1Amount > 0) {
                  // FIXED: was waveAmount
                  try {
                    await axios.put(
                      `${API_URL}/settings`,
                      {
                        wave1Amount,
                        wave2Amount: wave1Amount, // Auto-mirror
                      },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setWave2Amount(wave1Amount);
                    setOnboardingStep(1);
                  } catch (err) {
                    console.error("Failed to save wave amount:", err);
                    toast.error(t.onboardingSteps.saveWaveError);
                  }
                } else {
                  toast.error(t.onboardingSteps.validWaveAmount);
                }
              }}
              style={{
                width: "100%",
                padding: "0.75rem 1.5rem",
                background: "var(--primary-color)",
                border: "none",
                borderRadius: "8px",
                color: "white",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t.onboardingSteps.continueButton}
            </button>
          </div>
        )}
        {/* Step 2: Add Sample Expense */}
        {onboardingStep === 1 && (
          <div style={{ animation: "fadeIn 0.3s ease-in" }}>
            <div style={{ textAlign: "center", marginBottom: "1rem" }}>
              <ListPlus size={64} style={{ color: "var(--primary-color)" }} />
            </div>
            <h3 style={{ color: "var(--text-color)", marginBottom: "1rem" }}>
              {t.onboardingSteps.step2Title}
            </h3>
            <p
              style={{
                color: "var(--text-secondary)",
                lineHeight: 1.6,
                marginBottom: "1.5rem",
              }}
            >
              {t.onboardingSteps.step2Description}
            </p>

            {!showQuickAdd ? (
              <>
                <div
                  style={{
                    background: "var(--input-bg)",
                    padding: "1.5rem",
                    borderRadius: "12px",
                    border: "2px solid var(--primary-color)",
                    marginBottom: "1rem",
                  }}
                >
                  <p
                    style={{
                      color: "var(--primary-color)",
                      fontWeight: "bold",
                      marginBottom: "0.5rem",
                      fontSize: "0.95rem",
                    }}
                  >
                    ⚡ Quick Start with Templates
                  </p>
                  <p
                    style={{
                      color: "var(--text-secondary)",
                      fontSize: "0.9rem",
                      marginBottom: "1rem",
                    }}
                  >
                    Select common expenses to add instantly, or skip to add manually
                  </p>
                  <button
                    onClick={() => setShowQuickAdd(true)}
                    style={{
                      width: "100%",
                      padding: "0.75rem 1rem",
                      background: "var(--primary-color)",
                      border: "none",
                      borderRadius: "8px",
                      color: "white",
                      fontSize: "1rem",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    📝 Browse Templates
                  </button>
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <button
                    onClick={() => setOnboardingStep(0)}
                    style={{
                      flex: 1,
                      padding: "0.75rem 1rem",
                      background: "transparent",
                      border: "2px solid var(--border-color)",
                      borderRadius: "8px",
                      color: "var(--text-color)",
                      fontSize: "1rem",
                      cursor: "pointer",
                    }}
                  >
                    {t.onboardingSteps.backButton}
                  </button>
                  <button
                    onClick={() => setOnboardingStep(2)}
                    style={{
                      flex: 2,
                      padding: "0.75rem 1rem",
                      background: "var(--primary-color)",
                      border: "none",
                      borderRadius: "8px",
                      color: "white",
                      fontSize: "1rem",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    Skip & Continue →
                  </button>
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    marginBottom: "1rem",
                    background: "var(--input-bg)",
                    borderRadius: "12px",
                    padding: "1rem",
                  }}
                >
                  {popularTemplates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplateIds((prev) => {
                          const newSet = new Set(prev);
                          if (newSet.has(template.id)) {
                            newSet.delete(template.id);
                          } else {
                            newSet.add(template.id);
                          }
                          return newSet;
                        });
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.75rem",
                        marginBottom: "0.5rem",
                        borderRadius: "8px",
                        border: selectedTemplateIds.has(template.id)
                          ? "2px solid var(--primary-color)"
                          : "2px solid var(--border-color)",
                        background: selectedTemplateIds.has(template.id)
                          ? "var(--primary-color-10)"
                          : "var(--card-bg)",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTemplateIds.has(template.id)}
                        readOnly
                        style={{ marginRight: "0.75rem" }}
                      />
                      <span style={{ fontSize: "1.5rem", marginRight: "0.5rem" }}>
                        {template.icon}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "600", color: "var(--text-color)" }}>
                          {_language === 'es' ? template.nameEs : template.name}
                        </div>
                        {template.price && (
                          <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                            ${template.price.toFixed(2)}/mo
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
                  <button
                    onClick={handleAddQuickTemplates}
                    disabled={isAddingTemplates}
                    style={{
                      width: "100%",
                      padding: "0.75rem 1rem",
                      background: isAddingTemplates ? "var(--border-color)" : "var(--primary-color)",
                      border: "none",
                      borderRadius: "8px",
                      color: "white",
                      fontSize: "1rem",
                      fontWeight: 500,
                      cursor: isAddingTemplates ? "not-allowed" : "pointer",
                    }}
                  >
                    {isAddingTemplates
                      ? "Adding..."
                      : `Add ${selectedTemplateIds.size} Expense${selectedTemplateIds.size !== 1 ? 's' : ''} & Continue`}
                  </button>
                  <button
                    onClick={() => {
                      setShowQuickAdd(false);
                      setSelectedTemplateIds(new Set());
                    }}
                    style={{
                      width: "100%",
                      padding: "0.75rem 1rem",
                      background: "transparent",
                      border: "2px solid var(--border-color)",
                      borderRadius: "8px",
                      color: "var(--text-color)",
                      fontSize: "1rem",
                      cursor: "pointer",
                    }}
                  >
                    ← Back
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {/* Step 3: Explain Wave Budgeting */}
        {onboardingStep === 2 && (
          <div style={{ animation: "fadeIn 0.3s ease-in" }}>
            <div style={{ textAlign: "center", marginBottom: "1rem" }}>
              <Waves size={64} style={{ color: "var(--primary-color)" }} />
            </div>
            <h3 style={{ color: "var(--text-color)", marginBottom: "1rem" }}>
              {t.onboardingSteps.step3Title}
            </h3>
            <p
              style={{
                color: "var(--text-secondary)",
                lineHeight: 1.6,
                marginBottom: "1.5rem",
              }}
            >
              {t.onboardingSteps.step3Description}
            </p>
            <div
              style={{
                background:
                  "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 100%)",
                padding: "1.5rem",
                borderRadius: "12px",
                color: "white",
                marginBottom: "1.5rem",
              }}
            >
              <h4 style={{ marginTop: 0, fontSize: "1.1rem" }}>
                {t.onboardingSteps.howWavesWork}
              </h4>
              <ul style={{ lineHeight: 1.8, paddingLeft: "1.5rem" }}>
                <li>{t.onboardingSteps.paidBiweekly}</li>
                <li>{t.onboardingSteps.paidWeekly}</li>
                <li>{t.onboardingSteps.assignedToWave}</li>
                <li>{t.onboardingSteps.seeAtGlance}</li>
              </ul>
            </div>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                onClick={() => setOnboardingStep(1)}
                style={{
                  flex: 1,
                  padding: "0.75rem 1rem",
                  background: "transparent",
                  border: "2px solid var(--border-color)",
                  borderRadius: "8px",
                  color: "var(--text-color)",
                  fontSize: "1rem",
                  cursor: "pointer",
                }}
              >
                {t.onboardingSteps.backButton}
              </button>
              <button
                onClick={completeOnboarding}
                style={{
                  flex: 2,
                  padding: "0.75rem 1rem",
                  background: "var(--wave-3-bg)",
                  border: "none",
                  borderRadius: "8px",
                  color: "var(--wave-3-text)",
                  fontSize: "1rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {t.onboardingSteps.getStarted}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
